/**
 * The YouTube link parser behind ADD A VIDEO.
 *
 * This is the only thing in the app that mints a `video_key`. Everything
 * downstream — the `game_videos` CHECK constraint, the `youtube-nocookie`
 * embed URL, the `i.ytimg.com` thumbnail — assumes an exact 11-character id, so
 * the parser's contract is "return a key that is certainly one, or null".
 *
 * WHY `new URL()` AND NOT A REGEX. A single regex over the whole string is the
 * usual way to do this and it is the wrong way: `https://evil.test/?u=https://
 * youtu.be/dQw4w9WgXcQ` matches a naive host pattern, and so does
 * `https://youtube.com.evil.test/watch?v=…`. Parsing first and then comparing
 * `url.hostname` against an allowlist makes both of those impossible, because
 * the WHATWG parser — not our pattern — decides what the host is. The regex that
 * remains is applied only to an already-extracted id.
 *
 * The returned `url` is the member's own link, trimmed, not a rebuilt canonical
 * form: the row should always be traceable to what was pasted, and a timestamp
 * (`t=`) or share tag (`si=`) is part of what they meant. The embed URL is
 * derived from `key` at render time (`mediaVM`), so nothing downstream depends
 * on the shape of the stored link.
 *
 * Rejected on purpose: `http:` (the embed would be blocked as mixed content),
 * every host outside the allowlist, a playlist-only link (no single video to
 * play), and anything whose id is not exactly 11 URL-safe characters.
 */

import type { GameVideo } from './types.ts';

/** Mirrors `public.game_videos.url`'s `char_length(url) <= 300` CHECK. */
export const VIDEO_URL_MAX = 300;

/** Mirrors `public.game_videos.title`'s `char_length(title) <= 80` CHECK. */
export const VIDEO_TITLE_MAX = 80;

/**
 * Mirrors `public.game_videos.video_key`'s YouTube arm, character for character.
 *
 * Migration 3006 made that CHECK provider-conditional rather than looser, so
 * this stays exactly 11 — a blanket widening would have retired the guarantee
 * `ytEmbedUrl` and `ytThumbUrl` are built on.
 */
export const VIDEO_KEY_RE = /^[A-Za-z0-9_-]{11}$/;

/** Mirrors the Twitch arm of the same CHECK: a VOD id or a clip slug. */
export const TWITCH_KEY_RE = /^[A-Za-z0-9_-]{4,80}$/;

/**
 * `URL.hostname` is already lowercased and punycoded by the parser, so this
 * comparison needs no normalization of its own. `youtube-nocookie.com` is here
 * because it is what we embed with — a member who copies an embed URL out of
 * another page should not be told it is not a YouTube link.
 */
const HOSTS: ReadonlySet<string> = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
]);

/** Path prefixes that carry the id as the next segment. */
const PATH_FORMS: readonly string[] = ['shorts', 'live', 'embed', 'v'];

/**
 * Twitch hosts, same allowlist-after-parse discipline as `HOSTS`.
 *
 * `clips.twitch.tv` is separate from the others because its path shape is
 * different — the slug sits at the root rather than under a channel.
 */
const TWITCH_HOSTS: ReadonlySet<string> = new Set([
  'twitch.tv',
  'www.twitch.tv',
  'm.twitch.tv',
  'clips.twitch.tv',
]);

export interface ParsedVideo {
  provider: 'youtube' | 'twitch';
  key: string;
  /**
   * `vod` and `clip` embed through different Twitch parameters (`?video=` vs
   * `clips.twitch.tv/embed?clip=`), so the distinction has to survive parsing.
   * Always `'video'` for YouTube, which has one embed form.
   */
  kind: 'video' | 'vod' | 'clip';
  /** the trimmed original link */
  url: string;
}

/**
 * `raw` → a provider/key/url triple, or `null` for anything this app will not
 * embed. Never throws: a malformed string is a `null`, not an exception, because
 * every call site is a paste field.
 */
export function parseVideoUrl(raw: string): ParsedVideo | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed || trimmed.length > VIDEO_URL_MAX) return null;

  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    return null;
  }

  // `https:` only. `http:` would be upgraded or blocked as mixed content on the
  // real origin, and `javascript:` / `data:` never get past this line.
  if (u.protocol !== 'https:') return null;

  // Parse first, THEN pick the provider off the parsed hostname — the order the
  // module header argues for, extended rather than reorganised.
  if (TWITCH_HOSTS.has(u.hostname)) return parseTwitch(u, trimmed);
  if (!HOSTS.has(u.hostname)) return null;

  const key = keyFromUrl(u);
  if (!key || !VIDEO_KEY_RE.test(key)) return null;

  return { provider: 'youtube', kind: 'video', key, url: trimmed };
}

/**
 * `twitch.tv/videos/<digits>` → VOD; `twitch.tv/<channel>/clip/<slug>` and
 * `clips.twitch.tv/<slug>` → clip.
 *
 * A bare channel URL (`twitch.tv/somebody`) is deliberately NOT accepted: it is
 * a live stream, not a fixed piece of evidence about a game, and what it points
 * at changes under the crew.
 */
function parseTwitch(u: URL, trimmed: string): ParsedVideo | null {
  const seg = u.pathname.split('/').filter(Boolean);

  if (u.hostname === 'clips.twitch.tv') {
    const slug = seg.length === 1 ? seg[0] : null;
    if (!slug || !TWITCH_KEY_RE.test(slug)) return null;
    return { provider: 'twitch', kind: 'clip', key: slug, url: trimmed };
  }

  // /videos/<id> — digits only; Twitch VOD ids are numeric.
  if (seg.length === 2 && seg[0] === 'videos') {
    const id = seg[1] ?? '';
    if (!/^\d{4,80}$/.test(id)) return null;
    return { provider: 'twitch', kind: 'vod', key: id, url: trimmed };
  }

  // /<channel>/clip/<slug>
  if (seg.length === 3 && seg[1] === 'clip') {
    const slug = seg[2] ?? '';
    if (!TWITCH_KEY_RE.test(slug)) return null;
    return { provider: 'twitch', kind: 'clip', key: slug, url: trimmed };
  }

  return null;
}

function keyFromUrl(u: URL): string | null {
  // `/a/b/` → ['a','b']: the empty segments a leading or trailing slash produce
  // are dropped, so `/embed/KEY/` and `/embed/KEY` are the same link.
  const seg = u.pathname.split('/').filter(Boolean);

  if (u.hostname === 'youtu.be') {
    // The short form carries the id and nothing else; `youtu.be/` alone is not
    // a video, and `youtu.be/playlist?list=…` is not one either.
    return seg.length === 1 ? (seg[0] ?? null) : null;
  }

  // /watch?v=KEY — `searchParams.get` handles repeated and out-of-order
  // parameters for us, and returns the FIRST `v`, which is what YouTube plays.
  if (seg.length === 1 && seg[0] === 'watch') return u.searchParams.get('v');

  // /shorts/KEY, /live/KEY, /embed/KEY, /v/KEY
  if (seg.length === 2 && seg[0] !== undefined && PATH_FORMS.includes(seg[0])) {
    return seg[1] ?? null;
  }

  return null;
}

/** The embed the carousel renders. `youtube-nocookie` is deliberate. */
export function ytEmbedUrl(key: string): string {
  return 'https://www.youtube-nocookie.com/embed/' + key;
}

/** The still the carousel shows before a member opens the clip. */
export function ytThumbUrl(key: string): string {
  return 'https://i.ytimg.com/vi/' + key + '/hqdefault.jpg';
}

/**
 * The Twitch embed. Still an `<iframe>` and still no third-party `<script>` —
 * the same posture `ytEmbedUrl` keeps.
 *
 * **`parent` MUST be the serving hostname and cannot be a constant.** Twitch
 * refuses to render if it disagrees with the embedding page, accepts no
 * wildcards, and this app serves from four origins: `quarterdown.com`, the
 * `workers.dev` fallback, the staging worker, and `localhost` in dev. A
 * hard-coded parent blanks the player on three of them. `APP_BASE_URL` is not
 * the answer either — that variable lives in the Worker/Supabase environment,
 * not in the client bundle.
 *
 * Production must be https for Twitch to embed at all; localhost is exempt,
 * which is exactly the dev case.
 */
export function twitchEmbedUrl(kind: 'vod' | 'clip', key: string, host: string): string {
  const parent = encodeURIComponent(host);
  return kind === 'clip'
    ? 'https://clips.twitch.tv/embed?clip=' + encodeURIComponent(key) + '&parent=' + parent
    : 'https://player.twitch.tv/?video=' + encodeURIComponent(key) + '&parent=' + parent +
        '&autoplay=false';
}

/**
 * The hostname to hand Twitch as `parent`.
 *
 * Read at render time rather than captured at module load so a bundle served
 * from more than one origin cannot bake in the wrong one. Falls back to
 * `localhost`, which is the only host that works without https anyway.
 */
export function embedParentHost(): string {
  return typeof window !== 'undefined' && window.location?.hostname
    ? window.location.hostname
    : 'localhost';
}

/**
 * What `addVideo` sends to the repository — the parsed triple plus the member's
 * title. Kept next to the parser because the three fields must agree.
 */
export type VideoDraft = Pick<GameVideo, 'provider' | 'kind' | 'key' | 'url' | 'title'>;
